import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './Allowlist.css'

const getFlowLabel = (flowType) => {
  if (flowType === 'pre_checkin') return 'Voranmeldung (Web)'
  if (flowType === 'terminal')    return 'Kiosk'
  if (flowType.startsWith('terminal_')) {
    const rest = flowType.slice('terminal_'.length)
      .split('_')
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ')
    return `Kiosk – ${rest}`
  }
  return flowType.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
}

const getFieldLabel = (field) => {
  const raw = field.label
  if (!raw) return field.id
  if (typeof raw === 'string') return raw
  return raw.de || raw.en || Object.values(raw)[0] || field.id
}

const emptyEntry = () => ({ data: {}, notes: '', subflow_filter: '', valid_from: '', valid_until: '' })

const Allowlist = () => {
  const { apiFetch, isOwner, admin } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [entries,        setEntries]        = useState([])
  const [config,         setConfig]         = useState({ enabled: true })
  const [allSteps,      setAllSteps]      = useState([])
  const [loading,       setLoading]       = useState(false)
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [configSaving,   setConfigSaving]   = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(null)
  const [newEntry,       setNewEntry]       = useState(emptyEntry())
  const [editingId,      setEditingId]      = useState(null)
  const [editForm,       setEditForm]       = useState({})
  const [dragOver,       setDragOver]       = useState(false)
  const [search,         setSearch]         = useState('')

  const fileRef = useRef()

  // ── Tenant load ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
      }).catch(() => {})
    } else if (admin?.tenant_id) {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner, admin?.tenant_id])

  // ── Data load ─────────────────────────────────────────────────────────────
  const loadEntries = () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    apiFetch(`/api/admin/allowlist/${selectedTenant}`)
      .then(r => r.json())
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setError('Laden fehlgeschlagen'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!selectedTenant) return
    setLoading(true)
    setFieldsLoading(true)
    setError(null)

    Promise.all([
      apiFetch(`/api/admin/allowlist/${selectedTenant}`).then(r => r.json()),
      apiFetch(`/api/admin/allowlist-config/${selectedTenant}`).then(r => r.json()),
    ])
      .then(([entriesData, configData]) => {
        setEntries(Array.isArray(entriesData) ? entriesData : [])
        if (configData && !configData.error) setConfig(configData)
      })
      .catch(() => setError('Laden fehlgeschlagen'))
      .finally(() => setLoading(false))

    // All data_entry steps across every configured flow — source of truth for allowlistCheck fields
    apiFetch(`/api/admin/data-entry-steps/${selectedTenant}`)
      .then(r => r.json())
      .then(steps => { if (Array.isArray(steps)) setAllSteps(steps) })
      .catch(() => {})
      .finally(() => setFieldsLoading(false))
  }, [selectedTenant])

  const showSuccess = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 3500)
  }

  // ── Derived: fields marked allowlistCheck, grouped by flow (for display) ──
  const checkedByFlow = allSteps
    .map(s => ({
      flowType: s.flowType,
      fields:   (s.config?.fields || []).filter(f => f.allowlistCheck),
    }))
    .filter(g => g.fields.length > 0)

  // Deduplicated field list for the entry form / table (first occurrence wins)
  const checkedFields = (() => {
    const seen = new Set()
    const out  = []
    for (const step of allSteps) {
      for (const f of (step.config?.fields || [])) {
        if (f.allowlistCheck && !seen.has(f.id)) { seen.add(f.id); out.push(f) }
      }
    }
    return out
  })()

  // ── Remove allowlistCheck flags for a flow ────────────────────────────────
  const removeFlowFlags = async (flowType) => {
    if (!window.confirm(`Abgleich-Einstellungen für „${getFlowLabel(flowType)}" wirklich entfernen?`)) return
    setError(null)
    const res = await apiFetch(
      `/api/admin/allowlist-flags/${selectedTenant}/${encodeURIComponent(flowType)}`,
      { method: 'DELETE' },
    )
    if (res.ok) {
      setFieldsLoading(true)
      apiFetch(`/api/admin/data-entry-steps/${selectedTenant}`)
        .then(r => r.json())
        .then(steps => { if (Array.isArray(steps)) setAllSteps(steps) })
        .catch(() => {})
        .finally(() => setFieldsLoading(false))
      showSuccess(`Abgleich-Einstellungen für „${getFlowLabel(flowType)}" entfernt`)
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Entfernen fehlgeschlagen')
    }
  }

  // ── Config save (only enabled toggle) ────────────────────────────────────
  const saveConfig = async () => {
    setConfigSaving(true)
    setError(null)
    const res = await apiFetch(`/api/admin/allowlist-config/${selectedTenant}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    })
    if (res.ok) {
      showSuccess('Einstellungen gespeichert')
    } else {
      setError('Einstellungen konnten nicht gespeichert werden')
    }
    setConfigSaving(false)
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const hasAnyData = (entry) =>
    Object.values(entry.data || {}).some(v => String(v).trim())

  const setEntryField = (setter, id, value) =>
    setter(e => ({ ...e, data: { ...e.data, [id]: value } }))

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const addEntry = async () => {
    if (!hasAnyData(newEntry)) return
    setError(null)
    const res = await apiFetch(`/api/admin/allowlist/${selectedTenant}`, {
      method: 'POST',
      body: JSON.stringify({
        data:           newEntry.data,
        notes:          newEntry.notes,
        subflow_filter: newEntry.subflow_filter || '',
        valid_from:     newEntry.valid_from  || null,
        valid_until:    newEntry.valid_until || null,
      }),
    })
    if (res.ok) {
      const added = await res.json()
      setEntries(prev => [...prev, added])
      setNewEntry(emptyEntry())
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Hinzufügen fehlgeschlagen')
    }
  }

  const saveEdit = async (id) => {
    const res = await apiFetch(`/api/admin/allowlist/${selectedTenant}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        data:           editForm.data,
        notes:          editForm.notes,
        subflow_filter: editForm.subflow_filter || '',
        valid_from:     editForm.valid_from  || null,
        valid_until:    editForm.valid_until || null,
      }),
    })
    if (res.ok) {
      const updated = await res.json()
      setEntries(prev => prev.map(e => e.id === id ? updated : e))
      setEditingId(null)
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Speichern fehlgeschlagen')
    }
  }

  const deleteEntry = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return
    const res = await apiFetch(`/api/admin/allowlist/${selectedTenant}/${id}`, { method: 'DELETE' })
    if (res.ok) setEntries(prev => prev.filter(e => e.id !== id))
  }

  // ── CSV import ────────────────────────────────────────────────────────────
  const readFileText = (file) =>
    file.text().then(text => {
      if (!text.includes('â€')) return text
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload  = e => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(file, 'windows-1252')
      })
    })

  // Accepts any field label or ID from any form config step (not just allowlistCheck ones)
  const buildHeaderMap = () => {
    const map = {}
    for (const step of allSteps) {
      for (const f of (step.config?.fields || [])) {
        map[f.id.toLowerCase()]             = f.id
        map[getFieldLabel(f).toLowerCase()] = f.id
      }
    }
    return map
  }

  // Dynamic table columns: allowlistCheck fields first, then any extra keys in existing entries
  const tableColumns = (() => {
    const seen = new Set()
    const out  = []
    for (const f of checkedFields) {
      if (!seen.has(f.id)) { seen.add(f.id); out.push({ id: f.id, label: getFieldLabel(f) }) }
    }
    for (const entry of entries) {
      for (const key of Object.keys(entry.data || {})) {
        if (!seen.has(key)) {
          seen.add(key)
          let label = key
          for (const step of allSteps) {
            const f = (step.config?.fields || []).find(f => f.id === key)
            if (f) { label = getFieldLabel(f); break }
          }
          out.push({ id: key, label })
        }
      }
    }
    return out
  })()

  const isNotesHeader      = h => ['notes', 'notizen', 'firma', 'company'].includes(h.toLowerCase())
  const isSubflowHeader    = h => ['subflow_filter', 'subflow-filter', 'subflow', 'nur subflow', 'nur für subflow'].includes(h.toLowerCase())
  const isValidFromHeader  = h => h.toLowerCase() === 'valid_from' || h.toLowerCase().startsWith('gültig ab')
  const isValidUntilHeader = h => h.toLowerCase() === 'valid_until'|| h.toLowerCase().startsWith('gültig bis')

  const handleCSV = async (file) => {
    if (!file) return
    const text = await readFileText(file)
    const lines = text.split(/[\r\n]+/).map(l => l.trim()).filter(Boolean)
    if (lines.length < 2) { setError('Datei zu kurz – erste Zeile muss die Spaltenköpfe enthalten.'); return }

    const headers   = lines[0].split(/[,;\t]/).map(h => h.trim())
    const headerMap = buildHeaderMap()

    const notesIdx   = headers.findIndex(isNotesHeader)
    const subflowIdx = headers.findIndex(isSubflowHeader)
    const fromIdx    = headers.findIndex(isValidFromHeader)
    const untilIdx   = headers.findIndex(isValidUntilHeader)

    const parsed = lines.slice(1)
      .map(l => {
        const cols = l.split(/[,;\t]/).map(c => c.trim())
        const data = {}
        headers.forEach((h, i) => {
          if (isNotesHeader(h) || isSubflowHeader(h) || isValidFromHeader(h) || isValidUntilHeader(h)) return
          const fieldId = headerMap[h.toLowerCase()]
          if (fieldId && cols[i]) data[fieldId] = cols[i]
        })
        return {
          data,
          notes:          (notesIdx   >= 0 ? cols[notesIdx]   : '') || '',
          subflow_filter: (subflowIdx >= 0 ? cols[subflowIdx] : '') || '',
          valid_from:     (fromIdx    >= 0 ? cols[fromIdx]    : null) || null,
          valid_until:    (untilIdx   >= 0 ? cols[untilIdx]   : null) || null,
        }
      })
      .filter(e => Object.values(e.data).some(v => v))

    if (parsed.length === 0) { setError('Keine gültigen Einträge in der Datei gefunden.'); return }

    setError(null)
    setSaving(true)
    const res = await apiFetch(`/api/admin/allowlist/${selectedTenant}/import`, {
      method: 'POST',
      body: JSON.stringify({ entries: parsed }),
    })
    if (res.ok) {
      const result = await res.json()
      setEntries(Array.isArray(result) ? result : [])
      showSuccess(`${parsed.length} Einträge importiert`)
    } else {
      setError('CSV-Import fehlgeschlagen')
    }
    setSaving(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  const downloadTemplate = () => {
    if (checkedFields.length === 0) return
    const headers = [
      ...checkedFields.map(f => getFieldLabel(f)),
      'Firma',
      'Subflow',
    ]
    const csv  = '﻿' + headers.join(';') + '\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = 'personenzulassung_vorlage.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Exports the current list — same column layout as the import template
  // (plus valid_from/valid_until, which the importer also recognizes), so
  // an exported file can be edited and re-imported without reshaping it.
  const escapeCsv = (v) => {
    const s = String(v ?? '')
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }

  const exportEntries = () => {
    if (entries.length === 0) return
    const headers = [...tableColumns.map(c => c.label), 'Firma', 'Subflow', 'valid_from', 'valid_until']
    const rows = entries.map(e => [
      ...tableColumns.map(c => e.data?.[c.id] ?? ''),
      e.notes || '',
      e.subflow_filter || '',
      e.valid_from || '',
      e.valid_until || '',
    ].map(escapeCsv).join(';'))
    const csv  = '﻿' + [headers.join(';'), ...rows].join('\n') + '\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `personenzulassung_export_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Filter ────────────────────────────────────────────────────────────────
  const q = search.toLowerCase()
  const filtered = entries.filter(e => {
    if (!q) return true
    const data = e.data || {}
    return Object.values(data).some(v => String(v).toLowerCase().includes(q)) ||
      (e.notes || '').toLowerCase().includes(q)
  })

  const fmtDate = (val) => {
    if (!val) return '—'
    const d = new Date(val)
    return isNaN(d) ? '—' : d.toLocaleDateString('de-DE')
  }

  if (loading) return <div className="loading-state"><span className="spinner" /></div>

  const addGridCols = `repeat(${checkedFields.length}, 1fr) 1.2fr 1fr auto`

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Personenzulassung</h1>
          <p>Abgleichliste für den Check-in – z. B. erwartete Besucher, Lieferanten oder Referenznummern.</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {/* Tenant selector */}
      {isOwner && tenants.length > 0 && (
        <div className="card al-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Config */}
      <div className="card">
        <div className="al-config-header">
          <div className="section-title">Abgleich-Einstellungen</div>
          <label className="al-toggle">
            <input type="checkbox" checked={config.enabled ?? true}
              onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} />
            <span>Prüfung aktiv</span>
          </label>
        </div>

        {fieldsLoading ? (
          <div className="al-fields-hint"><span className="spinner spinner-sm" /> Felder werden geladen…</div>
        ) : checkedByFlow.length > 0 && (
          <div className="al-flow-groups">
            {checkedByFlow.map(({ flowType, fields }) => (
              <div key={flowType} className="al-flow-group">
                <span className="al-flow-label">{getFlowLabel(flowType)}</span>
                <div className="al-flow-fields">
                  {fields.map(f => (
                    <span key={f.id} className="al-flow-field-badge">
                      {getFieldLabel(f)}
                      <span className="al-field-id">{f.id}</span>
                    </span>
                  ))}
                </div>
                {isOwner && (
                  <button
                    className="al-flow-remove-btn"
                    title={`Abgleich-Einstellungen für „${getFlowLabel(flowType)}" entfernen`}
                    onClick={() => removeFlowFlags(flowType)}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        )}

        <button className="btn btn-primary btn-sm al-config-save"
          onClick={saveConfig} disabled={configSaving}>
          {configSaving ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Einstellungen speichern'}
        </button>
      </div>

      {/* CSV Import */}
      <div className="card">
        <div className="al-csv-header">
          <div className="section-title">CSV-Import</div>
          {checkedFields.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
              Vorlage herunterladen
            </button>
          )}
        </div>
        <p className="sm-hint">
          Fügt Einträge zur bestehenden Liste hinzu. Vorlage herunterladen, ausfüllen und hochladen.
        </p>
        <div
          className={`lp-drop-zone${dragOver ? ' lp-drop-zone--active' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleCSV(e.dataTransfer.files[0]) }}
          onClick={() => fileRef.current?.click()}
        >
          <span className="lp-drop-icon">📂</span>
          {saving
            ? <><span className="spinner" /><span>Importiere…</span></>
            : <span>CSV-Datei hierher ziehen oder klicken zum Auswählen</span>
          }
          <input ref={fileRef} type="file" accept=".csv,.txt" hidden
            onChange={e => handleCSV(e.target.files[0])} />
        </div>
      </div>

      {/* Entries */}
      <div className="card">
        <div className="al-list-header">
          <div className="section-title">
            Einträge{entries.length > 0 ? ` (${entries.length})` : ''}
          </div>
          <div className="al-list-actions">
            {entries.length > 5 && (
              <input className="form-control al-search" placeholder="Suchen…"
                value={search} onChange={e => setSearch(e.target.value)} />
            )}
            {entries.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={exportEntries}>
                Exportieren
              </button>
            )}
          </div>
        </div>

        {checkedFields.length === 0 ? (
          <div className="lp-empty">
            Im Flow Builder noch keine Felder mit „Personenzulassung" markiert.
          </div>
        ) : (
          <>
            {/* Add row */}
            <div className="al-add-grid" style={{ gridTemplateColumns: addGridCols }}>
              {checkedFields.map(field => (
                <input key={field.id} className="form-control"
                  placeholder={getFieldLabel(field)}
                  value={newEntry.data[field.id] || ''}
                  onChange={e => setEntryField(setNewEntry, field.id, e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addEntry()} />
              ))}
              <input className="form-control"
                placeholder="Firma"
                title="Wird beim Check-in als Firmenname vorausgefüllt"
                value={newEntry.notes}
                onChange={e => setNewEntry(f => ({ ...f, notes: e.target.value }))} />
              <input className="form-control"
                placeholder="Nur Subflow (z. B. ups)"
                title="Leer = gilt für alle Subflows. Wenn befüllt, wird der Eintrag nur in dem Subflow geprüft, dessen mode-Name hier steht."
                value={newEntry.subflow_filter}
                onChange={e => setNewEntry(f => ({ ...f, subflow_filter: e.target.value }))} />
              <button className="btn btn-primary al-add-btn"
                disabled={!hasAnyData(newEntry)}
                onClick={addEntry}>
                Hinzufügen
              </button>
            </div>

            {filtered.length === 0 ? (
              <div className="lp-empty">
                {entries.length === 0 ? 'Noch keine Einträge' : 'Keine Treffer für diese Suche'}
              </div>
            ) : (
              <table className="al-table">
                <thead>
                  <tr>
                    {tableColumns.map(c => <th key={c.id}>{c.label}</th>)}
                    <th title="Wird beim Check-in als Firmenname vorausgefüllt">Firma</th>
                    <th title="Leer = gilt für alle Subflows. Befüllt = nur für diesen Subflow (flow mode-Name).">Nur Subflow</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(e => (
                    <tr key={e.id}>
                      {editingId === e.id ? (
                        <>
                          {tableColumns.map(col => (
                            <td key={col.id}>
                              <input className="form-control form-control--sm"
                                placeholder={col.label}
                                value={editForm.data?.[col.id] || ''}
                                onChange={ev => setEntryField(setEditForm, col.id, ev.target.value)} />
                            </td>
                          ))}
                          <td>
                            <input className="form-control form-control--sm"
                              placeholder="Firma"
                              value={editForm.notes || ''}
                              onChange={ev => setEditForm(f => ({ ...f, notes: ev.target.value }))} />
                          </td>
                          <td>
                            <input className="form-control form-control--sm"
                              placeholder="Nur Subflow"
                              value={editForm.subflow_filter || ''}
                              onChange={ev => setEditForm(f => ({ ...f, subflow_filter: ev.target.value }))} />
                          </td>
                          <td className="lp-actions">
                            <button className="btn btn-primary btn-sm" onClick={() => saveEdit(e.id)}>Speichern</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Abbrechen</button>
                          </td>
                        </>
                      ) : (
                        <>
                          {tableColumns.map(col => (
                            <td key={col.id} className="lp-meta al-cell">
                              {e.data?.[col.id] || '—'}
                            </td>
                          ))}
                          <td className="lp-meta al-notes" title={e.notes || ''}>{e.notes || '—'}</td>
                          <td className="lp-meta al-notes" title={e.subflow_filter || ''}>{e.subflow_filter || '—'}</td>
                          <td className="lp-actions">
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setEditingId(e.id)
                                setEditForm({
                                  data:           { ...(e.data || {}) },
                                  notes:          e.notes || '',
                                  subflow_filter: e.subflow_filter || '',
                                })
                              }}>
                              Bearbeiten
                            </button>
                            <button className="btn btn-ghost btn-sm lp-btn-delete" onClick={() => deleteEntry(e.id)}>
                              Löschen
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {selectedTenant && (
        <VersionHistoryPanel table="allowlist_entries" tenantId={selectedTenant} onRestored={loadEntries} enableTimelineRestore />
      )}
    </div>
  )
}

export default Allowlist
